import React, { useState } from "react";
import { Button, Table } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";

import { Patient } from "../../../../types/healthcare/PatientTypes";
import { Invoice } from "../../../../types/finance/FinanceTypes";
import { fetchInvoices, fetchClinicSettings } from "../../../../api/finance/FinanceApi";
import { financeKeys } from "../../../../api/finance/financeQueryKeys";
import { canRaiseInvoices } from "../../../../utils/userPrefs/validatePermissions";
import { formatCents } from "../../../../utils/finance/money";
import {
	invoiceDisplayNumber,
	invoiceStatusPresentation,
} from "../../../../utils/finance/invoiceDisplay";
import { CreateInvoiceModal } from "../../../../components/finance/invoicing";
import { SectionCard, StatusTag, EmptyState, InlineLoading } from "../../../../components/shared";

/**
 * Patient-scoped invoicing inside the patient record's Billing tab — I9.
 *
 * Composition only: a "Raise invoice" button that opens the SHARED `CreateInvoiceModal` pre-filled
 * with (and locked to) this patient, plus a short list of the patient's most recent invoices.
 *
 * ── Self-contained on purpose ─────────────────────────────────────────────────────────────────
 *
 * It can be dropped into or out of `BillingTab` without touching anything else, and the whole
 * section is gated on `canRaiseInvoices()` — a clinician who cannot raise invoices sees nothing and
 * the section makes NO finance calls for them (the queries are `enabled: permitted`). The gate is
 * cosmetic; the real boundary is finance's own `finance.invoices.*` route declarations.
 */
interface PatientInvoicesSectionProps {
	patient: Patient;
}

const PatientInvoicesSection: React.FC<PatientInvoicesSectionProps> = ({ patient }) => {
	const permitted = canRaiseInvoices();
	const userId = patient?.id != null ? String(patient.id) : "";
	const listParams = { userId, per_page: 10, sort: "createdAt" as const, order: "desc" as const };

	const [modalOpen, setModalOpen] = useState(false);

	// Clinic defaults for the modal (entry mode, tax code, terms). Not gating the modal on it —
	// the server applies the same defaults for anything left undefined.
	const { data: settings } = useQuery({
		queryKey: financeKeys.clinicSettings(),
		queryFn: fetchClinicSettings,
		enabled: permitted,
		staleTime: 5 * 60 * 1000,
	});

	const { data, isLoading } = useQuery({
		queryKey: financeKeys.invoiceList(listParams),
		queryFn: () => fetchInvoices(listParams),
		enabled: permitted && userId !== "",
	});

	if (!permitted) {
		return null;
	}

	const invoices: Invoice[] = data?.data ?? [];

	const columns = [
		{
			title: "Number",
			key: "number",
			render: (_: unknown, inv: Invoice) => invoiceDisplayNumber(inv),
		},
		{ title: "Issued", dataIndex: "issueDate", key: "issueDate", width: 120 },
		{
			title: "Status",
			key: "status",
			width: 120,
			render: (_: unknown, inv: Invoice) => {
				const status = inv.effectiveStatus ?? inv.status;
				const presentation = invoiceStatusPresentation(status);
				return <StatusTag status={status} color={presentation.color} label={presentation.label} />;
			},
		},
		{
			title: "Total",
			key: "total",
			width: 110,
			align: "right" as const,
			render: (_: unknown, inv: Invoice) => formatCents(inv.totalIncCents),
		},
		{
			title: "Balance",
			key: "balance",
			width: 110,
			align: "right" as const,
			render: (_: unknown, inv: Invoice) => formatCents(inv.balanceDueCents),
		},
	];

	return (
		<SectionCard
			title="Invoices"
			extra={
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={() => setModalOpen(true)}
					data-testid="billing-raise-invoice"
				>
					Raise invoice
				</Button>
			}
		>
			{isLoading ? (
				<InlineLoading message="Loading invoices…" minHeight={120} />
			) : invoices.length === 0 ? (
				<EmptyState title="No invoices for this patient yet" />
			) : (
				<Table
					size="small"
					rowKey={(inv: Invoice) => inv.id}
					columns={columns}
					dataSource={invoices}
					pagination={false}
					data-testid="billing-invoices-table"
				/>
			)}

			<CreateInvoiceModal
				open={modalOpen}
				onClose={() => setModalOpen(false)}
				fixedUserId={userId}
				settings={settings ?? null}
			/>
		</SectionCard>
	);
};

export default PatientInvoicesSection;
